import classNames from 'classnames'; // Assuming classnames is available or can be added
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import './ChatButton.scss';

import { Icon } from 'ui';

const ChatButton = forwardRef(function ChatButton(
  { children, className, color, disabled, hasShadow, icon, iconPosition, onClick, size, variant, ...rest },
  ref
) {
  const baseClassName = 'chat-button';

  //   if variant is text or outlined, use the color of the button
  const iconColor = variant === 'text' || variant === 'outlined' ? color : 'white';

  const buttonClasses = classNames(
    baseClassName,
    `${baseClassName}--${variant}`,
    `${baseClassName}--${color}`,
    `${baseClassName}--${size}`,
    {
      [`${baseClassName}--has-shadow`]: hasShadow && variant !== 'text', // Shadow usually not applied to text buttons
      [`${baseClassName}--icon-only`]: iconPosition === 'iconOnly',
      [`${baseClassName}--icon-left`]: icon && iconPosition === 'left' && iconPosition !== 'iconOnly',
      [`${baseClassName}--icon-right`]: icon && iconPosition === 'right' && iconPosition !== 'iconOnly',
      // Disabled state class will be handled by SCSS pseudo-class :disabled
    },
    className
  );

  const renderIcon = () => {
    if (!icon) return null;
    // Assuming icon prop can be a string (icon name) or a node
    return typeof icon === 'string' ? <Icon name={icon} className={`${baseClassName}__icon`} color={iconColor} /> : icon;
  };

  const content =
    iconPosition === 'iconOnly' ? (
      renderIcon()
    ) : (
      <>
        {icon && iconPosition === 'left' && renderIcon()}
        {children && <span className={`${baseClassName}__text`}>{children}</span>}
        {icon && iconPosition === 'right' && renderIcon()}
      </>
    );

  return (
    <button
      ref={ref}
      className={buttonClasses}
      disabled={disabled}
      onClick={onClick}
      type="button" // Default to button type
      {...rest}
    >
      {content}
    </button>
  );
});

ChatButton.propTypes = {
  /** Button content (text or nodes) */
  children: PropTypes.node,
  /** Additional class names */
  className: PropTypes.string,
  /** Color theme of the button */
  color: PropTypes.oneOf(['brand']),
  /** Whether the button is disabled */
  disabled: PropTypes.bool,
  /** Whether the button has a shadow */
  hasShadow: PropTypes.bool,
  /** Icon element or name */
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Position of the icon relative to the text */
  iconPosition: PropTypes.oneOf(['left', 'right', 'iconOnly']),
  /** Click handler function */
  onClick: PropTypes.func,
  /** Size of the button */
  size: PropTypes.oneOf(['large', 'small']),
  /** Style variant of the button */
  variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
};

ChatButton.defaultProps = {
  children: null,
  className: '',
  color: 'brand',
  disabled: false,
  hasShadow: false,
  icon: null,
  iconPosition: 'left', // Default icon position if icon exists
  onClick: () => {},
  size: 'small',
  variant: 'contained',
};

export default ChatButton;
